import { Component } from "@angular/core";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";
import { AzureCostOptimizationBaseComponent } from "../azure-costoptimization-base.component";

@Component({
  selector: "app-azure-floating-data-cost-optimization",
  templateUrl: "./azure-floating-data-costoptimization.component.html",
  styleUrls: ["./azure-floating-data-costoptimization.component.css"],
})
export class AzureFloatingDataCostOptimizationComponent extends AzureCostOptimizationBaseComponent {
  pageHeading: string = "Floating Data Cost Control";
  recommendationSummary: string =
    "Scan and download reports for floating images, floating snapshots, and unattached managed disks.";
  protected scanAction: string = "floating_data";

  headers: any[] = [];
  currentPage: string = "scan";
  scanDetails: any = null;
  artifactPresignedUrls: any = {};
  artifactDownloadUrls: any = {};

  scanHeaders: any[] = [
    { id: "scan_id", name: "Scan ID" },
    { id: "status", name: "Status" },
    { id: "scanned_date_time_label", name: "Scanned On" },
    { id: "completed_aate_time_label", name: "Completed On" },
    { id: "floating_images_count", name: "Floating Images Count" },
    { id: "floating_disk_snapshots_count", name: "Floating Disk Snapshots Count" },
    { id: "unattached_managed_disks_count", name: "Unattached Managed Disks Count" },
  ];

  constructor(apiService: APIService, notifier: NotifierService) {
    super(apiService, notifier);
  }

  refresh(showGlobalLoader: boolean = false) {
    this.currentPage = "scan";
    this.scanDetails = null;
    this.artifactPresignedUrls = {};
    this.artifactDownloadUrls = {};
    super.refresh(showGlobalLoader);
  }

  protected getCreateScanBasePath(): string {
    return `${environment.apiURL}/${this.urlPrefix}/azure/costoptimization/api/optimization/floating-data-bundle-scan`;
  }

  protected getCreateScanParams(): any {
    return {
      user_id: this.userId,
      subscription_id: this.accountId,
      resource_group_name: this.resourceGroup,
    };
  }

  async loadScanDetails(scanId: string) {
    if (!scanId) {
      this.fetching = false;
      return;
    }
    const detailsUrl = `${this.azureCustomAnalysisBasePath}/scan-details?${this.buildQuery(
      {
        user_id: this.userId,
        scan_id: scanId,
        subscription_id: this.accountId,
        action: this.scanAction,
      }
    )}`;

    let result: any = await this.apiService.getDataPromis(
      detailsUrl,
      {},
      this.authHeader
    );

    if (this.destroyed) {
      return;
    }

    if (result && (result.status || result.items)) {
      this.vmOptimizations = result.items || [];
      this.scanDetails = this.normalizeScanItem(result);
      this.artifactPresignedUrls = result.artifact_presigned_urls || {};
      this.artifactDownloadUrls = result.artifact_download_urls || {};
      this.fetching = false;
    } else {
      this.vmOptimizations = [];
      this.scanDetails = null;
      this.artifactPresignedUrls = {};
      this.artifactDownloadUrls = {};
      const err = this.parseApiError(result);
      if (err.toLowerCase().indexOf("no record") == -1) {
        this.notifier.alert("Error", "", err, "error", 5000);
      }
      this.fetching = false;
    }
  }

  get scanInfo(): any {
    return {
      ...(this.jobDetails || {}),
      ...(this.scanDetails || {}),
    };
  }

  isScanCompleted(): boolean {
    return (this.jobDetails?.status || "").toLowerCase() == "completed";
  }

  isScanRunning(): boolean {
    const status = (this.jobDetails?.status || "").toLowerCase();
    return status != "" && status != "completed";
  }

  buildArtifactDownloadUrl(relativeUrl: string): string {
    if (!relativeUrl) {
      return null;
    }
    if (relativeUrl.indexOf("http://") == 0 || relativeUrl.indexOf("https://") == 0) {
      return relativeUrl;
    }
    const cleanPath = relativeUrl.indexOf("/") == 0 ? relativeUrl.slice(1) : relativeUrl;
    return `${environment.apiURL}/${this.urlPrefix}/azure/costoptimization/${cleanPath}`;
  }

  getArtifactUrl(artifactKey: string): string {
    if (this.artifactDownloadUrls && this.artifactDownloadUrls[artifactKey]) {
      return this.buildArtifactDownloadUrl(this.artifactDownloadUrls[artifactKey]);
    }
    if (this.artifactPresignedUrls && this.artifactPresignedUrls[artifactKey]) {
      return this.artifactPresignedUrls[artifactKey];
    }
    return null;
  }

  hasArtifact(artifactKey: string): boolean {
    return !!this.getArtifactUrl(artifactKey);
  }

  downloadArtifact(artifactKey: string) {
    const downloadUrl = this.getArtifactUrl(artifactKey);
    if (!downloadUrl) {
      this.notifier.alert("Info", "", "Download not available", "info", 5000);
      return;
    }
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.target = "_blank";
    link.rel = "noopener";
    link.download = `${artifactKey}.json`;
    link.click();
  }
}
